import React, { useState, useEffect } from 'react';

export interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: 'print' | 'word' | 'pdf' | 'text', scope: 'single' | 'all') => void;
  hasMultipleResponses: boolean;
  documentType?: string;
  mode?: 'print' | 'download' | 'both';
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
  isOpen,
  onClose,
  onExport,
  hasMultipleResponses,
  documentType = 'Report',
  mode = 'both'
}) => {
  const [format, setFormat] = useState<'print' | 'word' | 'pdf' | 'text'>('print');
  const [scope, setScope] = useState<'single' | 'all'>('single');

  // Reset format when mode changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      if (mode === 'print') {
        setFormat('print');
      } else if (mode === 'download') {
        setFormat('word');
      } else {
        setFormat('print');
      }
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const handleExport = () => {
    onExport(format, scope);
    // Reset to defaults for next time
    setFormat(mode === 'download' ? 'word' : 'print');
    setScope('single');
  };

  // Determine dialog title based on mode
  const dialogTitle = mode === 'print'
    ? `Print ${documentType}`
    : mode === 'download'
    ? `Export ${documentType}`
    : `Print or Export ${documentType}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {dialogTitle}
        </h3>

        {/* Format Selection */}
        <div className="mb-6 space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            {mode === 'print' ? 'Print options:' : mode === 'download' ? 'Choose export format:' : 'Choose output format:'}
          </p>

          {(mode === 'both' || mode === 'print') && (
            <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="format"
                value="print"
                checked={format === 'print'}
                onChange={(e) => setFormat(e.target.value as 'print' | 'word' | 'pdf' | 'text')}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">Print</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Open print dialog</div>
              </div>
            </label>
          )}

          {(mode === 'both' || mode === 'download') && (
            <>
              <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <input
                  type="radio"
                  name="format"
                  value="word"
                  checked={format === 'word'}
                  onChange={(e) => setFormat(e.target.value as 'print' | 'word' | 'pdf' | 'text')}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">Word Document</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Download as .docx file</div>
                </div>
              </label>

              <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={(e) => setFormat(e.target.value as 'print' | 'word' | 'pdf' | 'text')}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">PDF Document</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Download as .pdf file</div>
                </div>
              </label>

              <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <input
                  type="radio"
                  name="format"
                  value="text"
                  checked={format === 'text'}
                  onChange={(e) => setFormat(e.target.value as 'print' | 'word' | 'pdf' | 'text')}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">Text File</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Download as .txt file</div>
                </div>
              </label>
            </>
          )}
        </div>

        {/* Scope Selection (only if multiple responses) */}
        {hasMultipleResponses && (
          <div className="mb-6 space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Choose scope:
            </p>

            <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="scope"
                value="single"
                checked={scope === 'single'}
                onChange={(e) => setScope(e.target.value as 'single' | 'all')}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">This {documentType} Only</div>
              </div>
            </label>

            <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="scope"
                value="all"
                checked={scope === 'all'}
                onChange={(e) => setScope(e.target.value as 'single' | 'all')}
                className="mr-3"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">All {documentType}s</div>
              </div>
            </label>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleExport}
            className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
          >
            {format === 'print' ? 'Print' : 'Export'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
